import { SagaIterator } from 'redux-saga';
import { put, call, takeLatest, StrictEffect, select, delay, cancelled, cancel, race, take } from 'redux-saga/effects';

import {
  IWidgetData,
  fetchWidget,
  fetchWidgetError,
  fetchWidgetSuccess,
  saveWidgetData,
  saveWidgetDataSuccess,
  addEnabledWidgetSuccess,
  addEnabledWidget,
  IEnabledWidgetData,
  verifyWidget,
  verifyWidgetError,
  verifyWidgetSuccess,
  cancelVerifyWidget,
} from './index';
import { apiInstance } from 'src/services/interceptor';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { fetchSite } from '../site';

interface SiteId {
  siteId: string;
}

interface Data {
  data: IWidgetData;
}

interface EnabledData {
  data: IEnabledWidgetData;
}

function* getWidget({ payload }: PayloadAction<SiteId>): SagaIterator {
  try {
    const user = yield select((state) => state.user.data);
    if (payload.siteId) {
      const response = yield call(apiInstance, `tenant/${user.tenantId}/sites/${payload.siteId}/widget`);
      yield put(fetchWidgetSuccess(response.data));
      yield put(addEnabledWidgetSuccess(response.data));
    }
  } catch (e) {
    yield put(fetchWidgetError());
  }
}

function* getVerifyWidget({ payload }: any): any {
  try {
    const user = yield select((state) => state.user.data);
    const verifyResponse = yield call(apiInstance, `tenant/${user.tenantId}/sites/${payload.siteId}/verify`);
    if (verifyResponse.data.siteVerification === 'started') {
      while (true) {
        yield put(fetchSite({ id: payload.siteId }));
        yield delay(1000);

        const siteState = yield select((state) => state.site.data);
        if (siteState.isWidgetVerified !== null) {
          yield put(verifyWidgetSuccess(verifyResponse.data));
          break;
        }
      }
    } else {
      yield put(verifyWidgetError());
    }
  } catch (e) {
    yield put(verifyWidgetError());
  } finally {
    yield put(fetchSite({ id: payload.siteId }));

    if (yield cancelled()) {
      yield cancel();
    }
  }
}

function* getVerifyWidgetWithCancel({ payload }: any): SagaIterator {
  yield race([take(cancelVerifyWidget), call(getVerifyWidget, { payload })]);
}

function* updateWidget({ payload }: PayloadAction<Data & SiteId>): SagaIterator {
  try {
    const user = yield select((state) => state.user.data);
    yield call(apiInstance.put, `tenant/${user.tenantId}/sites/${payload.siteId}/widget`, payload.data);
    yield put(saveWidgetDataSuccess(payload.data));
  } catch (e) {
    yield put(fetchWidgetError());
  }
}

function* updateEnabled({ payload }: PayloadAction<EnabledData & SiteId>): SagaIterator {
  try {
    const user = yield select((state) => state.user.data);
    const response: AxiosResponse = yield call(
      apiInstance.put,
      `tenant/${user.tenantId}/sites/${payload.siteId}/widget/enabled`,
      payload.data,
    );
    yield put(addEnabledWidgetSuccess(response.data));
  } catch (e) {
    yield put(fetchWidgetError());
  }
}

function* widgetSaga(): Generator<StrictEffect, void> {
  yield takeLatest(fetchWidget.type, getWidget);
  yield takeLatest(saveWidgetData.type, updateWidget);
  yield takeLatest(addEnabledWidget.type, updateEnabled);
  yield takeLatest(verifyWidget.type, getVerifyWidgetWithCancel);
}

export { widgetSaga };
